import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import { Triangle } from "react-loader-spinner";
import { Button, Col, Input, InputGroup, Row } from "reactstrap";
import DeleteModal from "../../../Components/Common/DeleteModal";
import toast from "react-hot-toast";
import OrderManagementService from "../../../Services/QRCodeServices/Order Management/OrderManagementService";
import "./ReceivedOrder.css";
import EditOrderModal from "./EditOrderModal";

const ReceivedOrder = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const [editModal, setEditModal] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [editStatus, setEditStatus] = useState("");
  const [deleteModal, setDeleteModal] = useState(false);
  const toggleDeleteModal = () => setDeleteModal(!deleteModal);
  const [selectedDeleteItem, setSelectedDeleteItem] = useState(null);

  // Define status colors
  const statusColors = {
    Ongoing: "#FFA500", // Orange
    Received: "655bd1",
    Processing: "#fecb5d", // Yellow
    Ready: "#008000", // Green
    Delivering: "#800080", // Purple
    Cancelled: "#FF0000", // Red
    Completed: "#15c46a", // Bright Green
  };

  const handleDelete = async () => {
    if (selectedDeleteItem) {
      try {
        await OrderManagementService.delete(selectedDeleteItem.id);
        setData((prevList) =>
          prevList.filter((pkg) => pkg.id !== selectedDeleteItem.id)
        );
        toast.success("Selected Package deleted successfully");
        fetchOrder();
      } catch (error) {
        toast.error("Failed to delete the record");
      } finally {
        toggleDeleteModal();
        setSelectedDeleteItem(null);
      }
    }
  };

  const handleDeleteClick = (item) => {
    setSelectedDeleteItem(item);
    toggleDeleteModal();
  };

  const toggleEditModal = () => {
    setEditModal(!editModal);
    if (!editModal) {
      setSelectedOrder(null);
      setEditStatus("");
    }
  };

  const handleEditClick = async (item) => {
    try {
      const orderDetails = await OrderManagementService.detail(item.id);
      setSelectedOrder(orderDetails);
      setEditStatus(orderDetails.orderStatus);
      setEditModal(true);
    } catch (error) {
      toast.error("Failed to fetch order details");
    }
  };

  const handleSaveEdit = async (remarks) => {
    try {
      await OrderManagementService.updateStatus(selectedOrder.id, editStatus, remarks);
      toast.success("Order status updated successfully");
      fetchOrder();
      toggleEditModal();
    } catch (error) {
      toast.error("Failed to update the order status");
    }
  };

  const fetchOrder = async () => {
    setLoading(true);
    try {
      const response = await OrderManagementService.getList();
      setData(response);
      setFilteredData(response);
    } catch (error) {
      toast.error("Error fetching organizations");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrder();
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      fetchOrder();
    }, 120000);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const filtered = data?.filter((item) => {
      const title = item?.menuOrderDetails[0]?.menuItemDetail?.title || "";
      const matchesName = title.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesOrderRef = item.orderRef?.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesCustomerContact = item.customerContact?.toLowerCase().includes(searchQuery.toLowerCase());

      return matchesName || matchesOrderRef || matchesCustomerContact;
    });
    setFilteredData(filtered);
    setCurrentPage(1);
  }, [searchQuery, data]);

  const handleCardClick = (orderId) => {
    navigate(`/order-management/detail/${orderId}`);
  };

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const paginatedData = filteredData.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const totalPages = Math.ceil(filteredData.length / itemsPerPage);

  const renderPagination = () => {
    const maxPageButtons = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxPageButtons / 2));
    let endPage = Math.min(totalPages, startPage + maxPageButtons - 1);

    if (endPage - startPage + 1 < maxPageButtons) {
      startPage = Math.max(1, endPage - maxPageButtons + 1);
    }

    const pages = [];

    if (startPage > 1) {
      pages.push(
        <Button key={1} onClick={() => handlePageChange(1)}>
          1
        </Button>
      );
      if (startPage > 2) {
        pages.push(<span key="ellipsis-start">...</span>);
      }
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <Button
          key={i}
          className={`pagination-button ${currentPage === i ? "active" : ""}`}
          onClick={() => handlePageChange(i)}
        >
          {i}
        </Button>
      );
    }

    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        pages.push(<span key="ellipsis-end">...</span>);
      }
      pages.push(
        <Button key={totalPages} onClick={() => handlePageChange(totalPages)}>
          {totalPages}
        </Button>
      );
    }

    return (
      <div className="pagination-container gap-2 d-flex align-items-center">
        <Button
          disabled={currentPage === 1}
          onClick={() => handlePageChange(currentPage - 1)}
        >
          Prev
        </Button>
        {pages}
        <Button
          disabled={currentPage === totalPages}
          onClick={() => handlePageChange(currentPage + 1)}
        >
          Next
        </Button>
      </div>
    );
  };

  return (
    <div className="page-content">
      <div className="container-fluid">
        <BreadCrumb title="Received Order List" pageTitle="Received Order" />
      </div>

      <Col md={4} sm={6} xs={12} className="px-xs-1 mb-3">
        <InputGroup>
          <Input
            placeholder="Search by menu item name, order ref, or customer contact"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </InputGroup>
      </Col>

      <Row className="order-cards-container">
        {loading ? (
          <div className="loading-container d-flex flex-column justify-content-center align-items-center">
            <Triangle
              visible={true}
              height="80"
              width="80"
              color="#5B71B9"
              ariaLabel="triangle-loading"
            />
            <h5 className="mt-1">Loading...</h5>
          </div>
        ) : paginatedData.length > 0 ? (
          paginatedData.map((order) => (
            <Col key={order.id} md={6} lg={6} className="mb-4 gap-0">
              <div
                className="order-card-container shadow-lg rounded-lg d-flex gap-1 align-items-center"
                onClick={() => handleCardClick(order.id)}
                style={{ borderRight: `3rem solid ${statusColors[order.orderStatus]}` }} // Dynamic border color
              >
                <img
                  src={require("../../../assets/qrcode/delivery-service-illustrated_23-2148505081.png")}
                  alt="Delivery"
                  className="delivery-img-order"
                />
                <div className="order-card-details flex-grow-1 p-0 m-0">
                  <div className="justify-content-between d-flex w-100 align-items-center">
                    <h5 className="mb-1 text-primary">{order?.customerContact || "N/A"}</h5>
                    <div className="order-card-actions d-flex gap-2">
                      <i
                        className="ri-edit-line text-warning cursor-pointer fw-bold fs-5"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEditClick(order);
                        }}
                      ></i>
                      <i
                        className="ri-delete-bin-line fw-bold cursor-pointer text-danger fs-5"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteClick(order);
                        }}
                      ></i>
                    </div>
                  </div>
                  <p className="mb-1">
                    <strong>Order Ref:</strong> {order.orderRef}
                  </p>
                  <p className="mb-1">
                    <strong>Created Date:</strong>{" "}
                    {new Date(order.createdDate).toLocaleString()}
                  </p>
                  <div className="d-flex p-0 m-0 justify-content-between">
                    <p className="mb-0">
                      <strong>Total:</strong> {order.totalAmount}{" "}
                      {order.menuOrderDetails[0]?.menuItemDetail?.currency}
                    </p>
                    <p className="mb-0">
                      <strong>Item Count:</strong> {order.itemCount}
                    </p>
                  </div>
                </div>
                <span
                  className="order-status"
                  style={{ color: statusColors[order.orderStatus] === "#FFFFFF" ? "#FFFFFF" : "#FFFFFF" }} // Adjust text color for visibility
                >
                  {order.orderStatus}
                </span>
              </div>
            </Col>
          ))
        ) : (
          <p>No orders found.</p>
        )}
      </Row>

      <div className="d-flex justify-content-end mt-4">
        {renderPagination()}
      </div>

      <EditOrderModal
        isOpen={editModal}
        toggle={toggleEditModal}
        selectedOrder={selectedOrder}
        status={editStatus}
        setStatus={setEditStatus}
        onSave={handleSaveEdit}
      />

      <DeleteModal
        show={deleteModal}
        onDeleteClick={handleDelete}
        onCloseClick={toggleDeleteModal}
      />
    </div>
  );
};

export default ReceivedOrder;